import { isTypeform, type CustomForm } from '@/services/db/custom-form/entity';
import { facebookFormDisplayName, type FacebookLeadAdsForm } from '@/services/db/facebook-lead-ads/entity';
import { isEmptyString } from '@/utils/string';
import type { EventAttributes, JsonValue } from './event-data';

/**
 * The event a form sends when someone submits it, and the data it carries.
 *
 * A form-triggered campaign fires on one reserved event, `form_submit`. Its
 * data describes the form (its id, name and type) and then holds one attribute
 * per form field. A test submission has the same shape, so the campaign's
 * form selector and data filters read it the way they read a real one.
 */

/** The event every form submission sends. */
export const FORM_SUBMIT_EVENT = 'form_submit';

/** The `form_type` of a Facebook Lead Ads form, which the Facebook list does not store. */
const FACEBOOK_LEAD_ADS_FORM_TYPE = 'facebook_lead_ads';

/**
 * A form to send a test submission of.
 *
 * The workspace keeps its Facebook Lead Ads forms in a separate list from all
 * its other forms, and the two lists describe a form with different fields, so
 * the form keeps the name of the list it came from.
 */
export type SendEventForm = { kind: 'facebook'; form: FacebookLeadAdsForm } | { kind: 'custom'; form: CustomForm };

/**
 * One attribute of the submission.
 *
 * A locked field describes the form rather than an answer to it, so it is
 * shown but not typed into.
 */
export type FormEventField = {
  key: string;
  value: string;
  isLocked: boolean;
};

/** The form's name, as the workspace sees it. */
export function sendEventFormName(sendForm: SendEventForm): string {
  return sendForm.kind === 'facebook' ? facebookFormDisplayName(sendForm.form.facebook_form_name) : sendForm.form.name;
}

/** Where a sent submission is counted from in analytics. */
export function formEventSource(sendForm: SendEventForm): string {
  return sendForm.kind === 'facebook' ? 'Facebook Lead Ads' : 'Custom Forms';
}

/** The fields that describe the form. Every submission carries them. */
function formMetaFields(sendForm: SendEventForm): FormEventField[] {
  const { form_id: formId, form_type: formType } =
    sendForm.kind === 'facebook'
      ? { form_id: sendForm.form.facebook_form_id, form_type: FACEBOOK_LEAD_ADS_FORM_TYPE }
      : sendForm.form;

  return [
    { key: 'form_id', value: formId, isLocked: true },
    { key: 'form_name', value: sendEventFormName(sendForm), isLocked: true },
    { key: 'form_type', value: formType, isLocked: true },
  ];
}

/**
 * Every attribute of a test submission of the form, in the order a real one
 * lists them.
 *
 * A Facebook lead carries the lead's id, then one answer per question, keyed
 * by the question's key in lower case. Every other form carries one value per
 * field, which opens on the attribute the field is mapped to. A Typeform
 * submission also carries each field's label, under the field's key with
 * `_label` added.
 */
export function formEventFields(sendForm: SendEventForm): FormEventField[] {
  const meta = formMetaFields(sendForm);

  if (sendForm.kind === 'facebook') {
    return [
      ...meta,
      { key: 'lead_id', value: '', isLocked: false },
      ...sendForm.form.attr_maps.map((map) => ({
        key: map.facebook_question_key.toLowerCase(),
        value: '',
        isLocked: false,
      })),
    ];
  }

  const hasLabels = isTypeform(sendForm.form);
  return [
    ...meta,
    ...sendForm.form.attr_maps.flatMap((map) => {
      const field = { key: map.form_field_key, value: map.cio_attr, isLocked: false };
      return hasLabels
        ? [field, { key: `${map.form_field_key}_label`, value: map.form_field_label, isLocked: true }]
        : [field];
    }),
  ];
}

/**
 * A typed value, read as JSON when it parses and kept as text when it does
 * not. So `5` is sent as a number and `true` as a boolean.
 */
function typedValue(value: string): JsonValue {
  const trimmed = value.trim();
  try {
    const parsed: JsonValue = JSON.parse(trimmed);
    return parsed;
  } catch {
    return trimmed;
  }
}

/**
 * The submission's data as the JSON the event carries.
 *
 * A locked field is sent as the text it holds. A form id is often all digits,
 * and a real submission carries it as text, so a test submission carries it
 * as text too rather than as a number. Fields with no key are not attributes
 * and are left out.
 */
export function formEventFieldsToJson(fields: readonly FormEventField[]): string {
  // Built from entries rather than by assignment, so a key called
  // `__proto__` is an attribute rather than the object's prototype.
  const attributes: EventAttributes = Object.fromEntries(
    fields
      .filter((field) => !isEmptyString(field.key))
      .map((field) => [field.key.trim(), field.isLocked ? field.value : typedValue(field.value)]),
  );

  return JSON.stringify(attributes, null, 2);
}
